import { call, put, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  fetchMarketingCampaignsRequest,
  fetchMarketingCampaignsSuccess,
  fetchMarketingCampaignsFailure,
  searchMarketingCampaignsRequest,
  searchMarketingCampaignsSuccess,
  searchMarketingCampaignsFailure,
  fetchMarketingCampaignDetailsRequest,
  fetchMarketingCampaignDetailsSuccess,
  fetchMarketingCampaignDetailsFailure,
  createMarketingCampaignRequest,
  createMarketingCampaignSuccess,
  createMarketingCampaignFailure,
  updateMarketingCampaignRequest,
  updateMarketingCampaignSuccess,
  updateMarketingCampaignFailure,
  deleteMarketingCampaignRequest,
  deleteMarketingCampaignSuccess,
  deleteMarketingCampaignFailure,
  sendMarketingCampaignRequest,
  sendMarketingCampaignSuccess,
  sendMarketingCampaignFailure,
} from '../slices/marketingCampaign.slice';

import {
  getMarketingCampaigns,
  getMarketingCampaignById,
  createMarketingCampaign,
  updateMarketingCampaign,
  deleteMarketingCampaign,
  searchMarketingCampaigns,
  marketingCampaignService,
} from '@/services/marketingService';

import { 
  CreateMarketingCampaignDto,
  UpdateMarketingCampaignDto,
  FetchMarketingCampaignsOptions 
} from '@/types/models/marketing';
import { toApiError } from '@/lib/errorHelpers';

function* fetchMarketingCampaignsSaga(action: PayloadAction<FetchMarketingCampaignsOptions>): Generator<any, void, any> {
  try {
    const response: any = yield call(getMarketingCampaigns, action.payload);
    yield put(fetchMarketingCampaignsSuccess({
      data: response.data || response,
      pagination: response.pagination || {
        page: action.payload.page || 1,
        total: response.length || 0,
        pageSize: action.payload.page_size || 10,
        totalPages: Math.ceil((response.length || 0) / (action.payload.page_size || 10)),
      }
    }));
  } catch (error: any) {
    yield put(fetchMarketingCampaignsFailure(toApiError(error)));
  }
}

function* searchMarketingCampaignsSaga(action: PayloadAction<{ query: string; options?: FetchMarketingCampaignsOptions }>): Generator<any, void, any> {
  try {
    const { query, options = {} } = action.payload;
    const response: any = yield call(searchMarketingCampaigns, { query, ...options });
    yield put(searchMarketingCampaignsSuccess({
      data: response.data || response,
      pagination: response.pagination || {
        page: options.page || 1,
        total: response.length || 0,
        pageSize: options.page_size || 10,
        totalPages: Math.ceil((response.length || 0) / (options.page_size || 10)),
      }
    }));
  } catch (error: any) {
    yield put(searchMarketingCampaignsFailure(toApiError(error)));
  }
}

function* fetchMarketingCampaignDetailsSaga(action: PayloadAction<{ id: string }>): Generator<any, void, any> {
  try {
    const response: any = yield call(getMarketingCampaignById, action.payload.id);
    yield put(fetchMarketingCampaignDetailsSuccess(response));
  } catch (error: any) {
    yield put(fetchMarketingCampaignDetailsFailure(toApiError(error)));
  }
}

function* createMarketingCampaignSaga(action: PayloadAction<CreateMarketingCampaignDto>): Generator<any, void, any> {
  try {
  const response: any = yield call(createMarketingCampaign as any, action.payload);
    yield put(createMarketingCampaignSuccess(response));
  } catch (error: any) {
    yield put(createMarketingCampaignFailure(toApiError(error)));
  }
}

function* updateMarketingCampaignSaga(action: PayloadAction<{ id: string; data: UpdateMarketingCampaignDto }>): Generator<any, void, any> {
  try {
    const { id, data } = action.payload;
  const response: any = yield call(updateMarketingCampaign as any, id, data);
    yield put(updateMarketingCampaignSuccess(response));
  } catch (error: any) {
    yield put(updateMarketingCampaignFailure(toApiError(error)));
  }
}

function* deleteMarketingCampaignSaga(action: PayloadAction<{ id: string }>): Generator<any, void, any> {
  try {
    yield call(deleteMarketingCampaign, action.payload.id);
    yield put(deleteMarketingCampaignSuccess({ id: action.payload.id }));
  } catch (error: any) {
    yield put(deleteMarketingCampaignFailure(toApiError(error)));
  }
}

function* sendMarketingCampaignSaga(action: PayloadAction<{ id: string }>): Generator<any, void, any> {
  try {
    const response: any = yield call(marketingCampaignService.sendCampaign, action.payload.id);
    yield put(sendMarketingCampaignSuccess(response));
  } catch (error: any) {
    yield put(sendMarketingCampaignFailure(toApiError(error)));
  }
}

export function* marketingCampaignSaga(): Generator<any, void, any> {
  yield takeLatest(fetchMarketingCampaignsRequest.type, fetchMarketingCampaignsSaga);
  yield takeLatest(searchMarketingCampaignsRequest.type, searchMarketingCampaignsSaga);
  yield takeLatest(fetchMarketingCampaignDetailsRequest.type, fetchMarketingCampaignDetailsSaga);
  yield takeLatest(createMarketingCampaignRequest.type, createMarketingCampaignSaga);
  yield takeLatest(updateMarketingCampaignRequest.type, updateMarketingCampaignSaga);
  yield takeLatest(deleteMarketingCampaignRequest.type, deleteMarketingCampaignSaga);
  yield takeLatest(sendMarketingCampaignRequest.type, sendMarketingCampaignSaga);
}

export default marketingCampaignSaga;